export interface AppConfig {
  msalClientId: string;
  msalAuthority: string;
  msalRedirectUri: string;
  msalScopes: string[];
  apiBaseUrl: string;
  appInsightsConnectionString: string;
  featureFlagsEndpoint: string;
  environment: string;
  featureFlags?: Record<string, boolean>;
  /**
   * Auth mode override. Absent → 'stub' when environment === 'local', else 'msal'.
   * 'stub' is honored only when environment === 'local' (fail-closed elsewhere) —
   * see resolveAuthMode in @/auth/authMode.
   */
  authMode?: 'msal' | 'stub';
  /**
   * Dev-only: start the MSW browser worker so the app runs without a backend.
   * Ignored in production builds. Cannot be combined with authMode 'msal'.
   */
  mockApi?: boolean;
}

function isAppConfig(value: unknown): value is AppConfig {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.msalClientId === 'string' &&
    typeof obj.msalAuthority === 'string' &&
    typeof obj.msalRedirectUri === 'string' &&
    Array.isArray(obj.msalScopes) &&
    obj.msalScopes.every((v: unknown) => typeof v === 'string') &&
    typeof obj.apiBaseUrl === 'string' &&
    typeof obj.appInsightsConnectionString === 'string' &&
    typeof obj.featureFlagsEndpoint === 'string' &&
    typeof obj.environment === 'string' &&
    (obj.authMode === undefined || obj.authMode === 'msal' || obj.authMode === 'stub') &&
    (obj.mockApi === undefined || typeof obj.mockApi === 'boolean')
  );
}

let cachedConfig: AppConfig | null = null;
let inflightRequest: Promise<AppConfig> | null = null;

export async function loadConfig(): Promise<AppConfig> {
  if (cachedConfig) return cachedConfig;
  if (inflightRequest) return inflightRequest;

  inflightRequest = (async () => {
    const response = await fetch('/config/config.json');
    if (!response.ok) {
      throw new Error(
        `Failed to load config: ${response.status}. ` +
          'Ensure public/config/config.json exists (copy from config.template.json).',
      );
    }

    let data: unknown;
    try {
      data = await response.json();
    } catch {
      throw new Error('config.json contains invalid JSON.');
    }

    if (!isAppConfig(data)) {
      throw new Error(
        'Invalid config.json shape. Compare with config.template.json for required fields.',
      );
    }

    cachedConfig = data;
    return data;
  })();

  try {
    return await inflightRequest;
  } finally {
    inflightRequest = null;
  }
}

/**
 * Synchronous accessor for the config cache. Returns null if config hasn't loaded yet.
 * Use this in non-async contexts where fail-closed behavior is preferred over async.
 */
export function getConfigCache(): AppConfig | null {
  return cachedConfig;
}

/** @internal Test-only: reset cached config between test cases */
export function _resetConfigCache(): void {
  cachedConfig = null;
  inflightRequest = null;
}
